// COMPLETE EXACT TEMPLATE IMPLEMENTATION
// This file contains the exact implementation of desktop-executive-dashboard.html

import { createSidebarNavigation } from '../components/SharedSidebarNavigation.js';

export function createExactDesktopDashboard(scene, dashboardData, initials, firmBadges) {
    console.log('🖥️ Creating EXACT template desktop dashboard...');
    
    // CRITICAL: Check if we're about to launch a fullscreen game (PPT Hero or Philosophy Flow)
    const isLaunchingFullscreenGame = window.pptHeroLaunching || window.philosophyFlowLaunching;
    if (isLaunchingFullscreenGame) {
        console.log('🎮 SKIPPING dashboard creation - launching fullscreen game');
        
        // Return minimal placeholder to prevent errors
        const placeholder = document.createElement('div');
        placeholder.id = 'game-mode-placeholder';
        placeholder.style.display = 'none';
        document.body.appendChild(placeholder);
        return placeholder;
    }
    
    // Remove any existing overlay
    scene.removeExistingOverlay();
    
    // Create main overlay with EXACT template styling
    const overlay = document.createElement('div');
    overlay.id = 'auth-menu-dom-overlay';
    overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        z-index: 10000;
        overflow: hidden;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        color: rgba(255, 255, 255, 0.9);
        box-sizing: border-box;
    `;
    
    // EXACT template HTML structure
    overlay.innerHTML = _getDesktopDashboardHTML(dashboardData, initials, firmBadges);
    
    // Load EXACT template CSS with proper desktop separation
    if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
        console.log('🎨 Loading EXACT desktop template CSS...');
        const link = document.createElement('link');
        link.id = 'desktop-executive-dashboard-exact-css';
        link.rel = 'stylesheet';
        link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter - webpack handles cache busting
        link.onload = () => console.log('🎨 EXACT desktop template CSS loaded successfully');
        link.onerror = () => {
            console.error('🚨 EXACT desktop template CSS failed to load - loading fallback');
            // Load fallback CSS
            const fallbackLink = document.createElement('link');
            fallbackLink.rel = 'stylesheet';
            fallbackLink.href = '/glass-morphism-ui.css?v=' + Date.now();
            document.head.appendChild(fallbackLink);
        };
        document.head.appendChild(link);
    } else {
        console.log('🎨 EXACT desktop template CSS already loaded');
    }
    
    // CRITICAL: Add inline CSS to ensure buttons are always clickable
    // This fixes the issue where external CSS fails to load from webpack dev server
    const criticalCSS = document.createElement('style');
    criticalCSS.id = 'critical-dashboard-css';
    criticalCSS.textContent = `
        /* CRITICAL: Ensure dashboard is visible and interactive */
        #auth-menu-dom-overlay {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            pointer-events: auto !important;
        }
        
        /* CRITICAL: Ensure sidebar navigation is visible and clickable */
        #auth-menu-dom-overlay .sidebar {
            display: flex !important;
            flex-direction: column !important;
            visibility: visible !important;
            pointer-events: auto !important;
        }
        
        #auth-menu-dom-overlay .nav-menu {
            display: block !important;
            visibility: visible !important;
            pointer-events: auto !important;
        }
        
        #auth-menu-dom-overlay .nav-item {
            display: flex !important;
            visibility: visible !important;
            pointer-events: auto !important;
            cursor: pointer !important;
            padding: 12px 16px !important;
            color: rgba(255, 255, 255, 0.7) !important;
            text-decoration: none !important;
            transition: all 0.3s ease !important;
        }
        
        #auth-menu-dom-overlay .nav-item:hover {
            background: rgba(255, 255, 255, 0.05) !important;
            color: rgba(255, 255, 255, 1) !important;
        }
        
        /* CRITICAL: Ensure main content is visible */
        #auth-menu-dom-overlay .main-content {
            display: flex !important;
            visibility: visible !important;
            pointer-events: auto !important;
        }
    `;
    document.head.appendChild(criticalCSS);
    console.log('🎨 Critical inline CSS added to ensure buttons work');
    
    document.body.appendChild(overlay);
    
    // Bind event listeners for template elements
    setTimeout(() => {
        scene.bindExactTemplateEvents();
        scene.sceneReady = true;
        console.log('🖥️ EXACT template dashboard ready');
    }, 100);
    
    return overlay;
}

function _getDesktopDashboardHTML(dashboardData, initials, firmBadges) {
    // Use shared sidebar component for 100% consistency
    const sidebar = createSidebarNavigation({
        activeScene: 'dashboard',
        userInitials: initials,
        userName: dashboardData.displayName,
        userRole: dashboardData.userTitle || 'Executive'
    });
    
    return `
        <!-- Animated Background -->
        <div class="background-effects">
            <div class="bg-grid"></div>
            <div class="bg-glow bg-glow-1"></div>
            <div class="bg-glow bg-glow-2"></div>
        </div>
        
        <!-- Layout Container -->
        <div class="app-container">
            <!-- Sidebar Navigation (Shared Component - Single Source of Truth) -->
            ${sidebar}
            
            <!-- Main Content -->
            <main class="main-content">
                <!-- Header -->
                <header class="header">
                    <div class="header-left">
                        <h1 class="page-title">Executive Dashboard</h1>
                        <nav class="breadcrumb">
                            <span>Home</span>
                            <span class="breadcrumb-separator">/</span>
                            <span>Dashboard</span>
                        </nav>
                    </div>
                    <div class="header-right">
                        <button class="header-btn" data-action="search">
                            🔍
                        </button>
                        <button class="header-btn" data-action="notifications">
                            <span>🔔</span>
                            <span class="notification-dot"></span>
                        </button>
                        <button class="header-btn" data-action="settings">
                            ⚙️
                        </button>
                    </div>
                </header>
                
                <!-- Dashboard Content -->
                <div class="dashboard-content">
                    <!-- Stats Grid -->
                    <div class="stats-grid">
                        <div class="stat-card">
                            <div class="stat-icon">📊</div>
                            <div class="stat-value">${dashboardData.overallStats.totalScore || 2847}</div>
                            <div class="stat-label">Total Score</div>
                            <span class="stat-change positive">↑ 12% this week</span>
                        </div>
                        
                        <div class="stat-card">
                            <div class="stat-icon">🏆</div>
                            <div class="stat-value">#${dashboardData.overallStats.globalRank || 12}</div>
                            <div class="stat-label">Global Rank</div>
                            <span class="stat-change positive">↑ 3 positions</span>
                        </div>
                        
                        <div class="stat-card">
                            <div class="stat-icon">🎯</div>
                            <div class="stat-value">${Math.round(dashboardData.overallStats.accuracy || 94)}%</div>
                            <div class="stat-label">Accuracy Rate</div>
                            <span class="stat-change positive">↑ 2.3%</span>
                        </div>
                        
                        <div class="stat-card">
                            <div class="stat-icon">⏱️</div>
                            <div class="stat-value">18.5h</div>
                            <div class="stat-label">Training Time</div>
                            <span class="stat-change negative">↓ 1.2h</span>
                        </div>
                    </div>
                    
                    <!-- Content Row -->
                    <div class="content-row">
                        <!-- Game Modules -->
                        <div class="content-card">
                            <div class="card-header">
                                <h2 class="card-title">Training Modules</h2>
                                <div class="card-actions">
                                    <button class="action-btn">View All</button>
                                </div>
                            </div>
                            
                            <div class="game-grid">
                                <div class="game-card" data-game="ppt-hero">
                                    <div class="game-header">
                                        <span class="game-icon-large">📈</span>
                                        <span class="game-status">Active</span>
                                    </div>
                                    <h3 class="game-title">PPT Hero</h3>
                                    <p class="game-description">Master PowerPoint shortcuts and presentation workflows</p>
                                    <div class="game-progress">
                                        <div class="progress-bar">
                                            <div class="progress-fill" style="width: 68%"></div>
                                        </div>
                                    </div>
                                    <div class="game-stats">
                                        <span>Level 5/10</span>
                                        <span>68% Complete</span>
                                    </div>
                                </div>
                                
                                <div class="game-card" data-game="philosophy-flow">
                                    <div class="game-header">
                                        <span class="game-icon-large">🎨</span>
                                        <span class="game-status">Active</span>
                                    </div>
                                    <h3 class="game-title">Philosophy Flow</h3>
                                    <p class="game-description">Strategic thinking through emotion and color</p>
                                    <div class="game-progress">
                                        <div class="progress-bar">
                                            <div class="progress-fill" style="width: 42%"></div>
                                        </div>
                                    </div>
                                    <div class="game-stats">
                                        <span>Level 3/10</span>
                                        <span>42% Complete</span>
                                    </div>
                                </div>
                                
                                <div class="game-card" data-game="slide-wars">
                                    <div class="game-header">
                                        <span class="game-icon-large">⚔️</span>
                                        <span class="game-status">Coming Soon</span>
                                    </div>
                                    <h3 class="game-title">Slide Wars</h3>
                                    <p class="game-description">Competitive real-time presentation battles</p>
                                    <div class="game-progress">
                                        <div class="progress-bar">
                                            <div class="progress-fill" style="width: 0%"></div>
                                        </div>
                                    </div>
                                    <div class="game-stats">
                                        <span>Coming Soon</span>
                                        <span>0% Complete</span>
                                    </div>
                                </div>
                                
                                <div class="game-card" data-game="classify-tech">
                                    <div class="game-header">
                                        <span class="game-icon-large">🏛️</span>
                                        <span class="game-status">Coming Soon</span>
                                    </div>
                                    <h3 class="game-title">Classify Tech</h3>
                                    <p class="game-description">Categorize technology and business concepts</p>
                                    <div class="game-progress">
                                        <div class="progress-bar">
                                            <div class="progress-fill" style="width: 0%"></div>
                                        </div>
                                    </div>
                                    <div class="game-stats">
                                        <span>Coming Soon</span>
                                        <span>0% Complete</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Leaderboard -->
                        <div class="content-card">
                            <div class="card-header">
                                <h2 class="card-title">Global Leaderboard</h2>
                                <div class="card-actions">
                                    <button class="action-btn" data-action="hall-of-fame">View All</button>
                                </div>
                            </div>
                            
                            <div class="leaderboard">
                                <div class="leaderboard-item">
                                    <div class="rank gold">1</div>
                                    <div class="leader-info">
                                        <div class="leader-name">Sarah Chen</div>
                                        <div class="leader-company">McKinsey & Co</div>
                                    </div>
                                    <div class="leader-score">
                                        <div class="score-value">4,892</div>
                                        <div class="score-label">points</div>
                                    </div>
                                </div>
                                
                                <div class="leaderboard-item">
                                    <div class="rank silver">2</div>
                                    <div class="leader-info">
                                        <div class="leader-name">Michael Park</div>
                                        <div class="leader-company">Bain & Company</div>
                                    </div>
                                    <div class="leader-score">
                                        <div class="score-value">4,567</div>
                                        <div class="score-label">points</div>
                                    </div>
                                </div>
                                
                                <div class="leaderboard-item">
                                    <div class="rank bronze">3</div>
                                    <div class="leader-info">
                                        <div class="leader-name">Emily Rodriguez</div>
                                        <div class="leader-company">BCG</div>
                                    </div>
                                    <div class="leader-score">
                                        <div class="score-value">4,234</div>
                                        <div class="score-label">points</div>
                                    </div>
                                </div>
                                
                                <div class="leaderboard-item">
                                    <div class="rank">4</div>
                                    <div class="leader-info">
                                        <div class="leader-name">James Wilson</div>
                                        <div class="leader-company">Deloitte</div>
                                    </div>
                                    <div class="leader-score">
                                        <div class="score-value">3,987</div>
                                        <div class="score-label">points</div>
                                    </div>
                                </div>
                                
                                <div class="leaderboard-item">
                                    <div class="rank">5</div>
                                    <div class="leader-info">
                                        <div class="leader-name">${dashboardData.displayName}</div>
                                        <div class="leader-company">${dashboardData.firmInfo.name}</div>
                                    </div>
                                    <div class="leader-score">
                                        <div class="score-value">${dashboardData.overallStats.totalScore || 3141}</div>
                                        <div class="score-label">points</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    `;
}

export function bindExactTemplateEvents(scene) {
    // Bind navigation events
    const navItems = document.querySelectorAll('.nav-item[data-action]');
    navItems.forEach(item => {
        item.addEventListener('click', (e) => {
            e.preventDefault();
            const action = e.currentTarget.dataset.action;
            scene.handleTemplateAction(action);
        });
    });
    
    // Bind game events
    const gameItems = document.querySelectorAll('.nav-item[data-game], .game-card[data-game]');
    gameItems.forEach(item => {
        item.addEventListener('click', (e) => {
            e.preventDefault();
            const game = e.currentTarget.dataset.game;
            scene.handleGameAction(game);
        });
    });
    
    // Bind header button events
    const headerBtns = document.querySelectorAll('.header-btn[data-action]');
    headerBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const action = e.currentTarget.dataset.action;
            scene.handleTemplateAction(action);
        });
    });
    
    console.log('🔗 EXACT template events bound successfully');
}
